import { useTranslation } from 'next-i18next'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { getLocaleConfig } from '@src/config/localisation/statsig-locale-config-constants'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { setJoinWaitlistPhoneNumber } from '@src/quince-site-common/src/utils/join-waitlist-phone-storage'
import {
  clearWaitlistSmsModalSuppression,
  recordWaitlistSmsModalDismiss,
} from '@src/quince-site-common/src/utils/waitlist-sms-suppression'
import { validatePhone } from '@src/quince-site-integrated/src/utils/validation'

import Button, { BUTTON_SIZE, BUTTON_TYPES } from '../button'
import Modal from '../modal'

import {
  WAITLIST_SMS_VENDOR_PRIVACY_URL,
  WAITLIST_SMS_VENDOR_PRIVACY_URL_UK,
  WAITLIST_SMS_VENDOR_TERMS_URL,
  WAITLIST_SMS_VENDOR_TERMS_URL_UK,
} from './waitlist-sms-modal.constants'
import styles from './waitlist-sms-modal.module.scss'

import type { WaitlistSmsModalProps } from './waitlist-sms-modal.types'

const PHONE_CONFIG_BY_LOCALE = {
  'en-us': { format: 'us', maxLength: 10, placeholder: '(555) 000-0000' },
  'en-gb': { format: 'eu', maxLength: 11, placeholder: '07911 123456' },
  'en-ca': { format: 'us', maxLength: 10, placeholder: '(555) 000-0000' },
}

const formatPhoneNumber = (rawDigits: string, format?: string): string => {
  const digits = rawDigits.replace(/\D/g, '')

  if (format === 'us') {
    // US/CA format: (555) 000-0000
    const maxDigits = digits.slice(0, 10)
    if (maxDigits.length === 0) return ''
    if (maxDigits.length < 4) return `(${maxDigits}`
    if (maxDigits.length < 7) return `(${maxDigits.slice(0, 3)}) ${maxDigits.slice(3)}`
    return `(${maxDigits.slice(0, 3)}) ${maxDigits.slice(3, 6)}-${maxDigits.slice(6)}`
  }

  // Non-US format: display with spaces (e.g., UK: 07911 123456)
  const maxDigits = digits.slice(0, 11)
  if (maxDigits.length === 0) return ''
  if (maxDigits.length <= 5) return maxDigits
  // Add space after first 5 digits for readability
  return `${maxDigits.slice(0, 5)} ${maxDigits.slice(5)}`
}

const extractDigits = (value: string): string => value.replace(/\D/g, '')

const AUTO_CLOSE_AFTER_MS = 20 * 60 * 1000

const RESIZE_TOTAL_MS = 460

type Phase = 'capture' | 'resizing' | 'success'

const WaitlistSmsModalLegacy = ({ open, onClose, onSubmitPhone }: WaitlistSmsModalProps) => {
  const { t } = useTranslation('common')
  const { locale } = useLocaleContext()
  const phoneConfig =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    PHONE_CONFIG_BY_LOCALE[locale as keyof typeof PHONE_CONFIG_BY_LOCALE] || PHONE_CONFIG_BY_LOCALE['en-us']
  const [phone, setPhone] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [phase, setPhase] = useState<Phase>('capture')

  const captureRef = useRef<HTMLDivElement>(null)
  const successRef = useRef<HTMLDivElement>(null)
  const phaseRef = useRef<Phase>('capture')
  const [heights, setHeights] = useState<{ capture: number; success: number } | null>(null)

  const isSuccess = phase === 'success'

  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  const resetModalState = () => {
    setPhone('')
    setErrorMessage('')
    setPhase('capture')
    setHeights(null)
  }

  const handleClose = () => {
    if (isSuccess) {
      clearWaitlistSmsModalSuppression()
    } else {
      recordWaitlistSmsModalDismiss()
    }
    resetModalState()
    onClose()
  }

  const handleAutoClose = () => {
    if (phaseRef.current !== 'success') {
      recordWaitlistSmsModalDismiss()
    }
    resetModalState()
    onClose()
  }

  useEffect(() => {
    if (!open) return
    const timerId = window.setTimeout(handleAutoClose, AUTO_CLOSE_AFTER_MS)
    return () => {
      window.clearTimeout(timerId)
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open) return

    const measure = () => {
      const capture = captureRef.current?.offsetHeight
      const success = successRef.current?.offsetHeight
      setHeights(prev => ({
        capture: capture && capture > 0 ? capture : (prev?.capture ?? 0),
        success: success && success > 0 ? success : (prev?.success ?? 0),
      }))
    }

    measure()

    const observer = new ResizeObserver(measure)
    if (captureRef.current) observer.observe(captureRef.current)
    if (successRef.current) observer.observe(successRef.current)
    return () => {
      observer.disconnect()
    }
  }, [open])

  useEffect(() => {
    if (phase !== 'resizing') return
    const timerId = window.setTimeout(() => {
      setPhase('success')
    }, RESIZE_TOTAL_MS)
    return () => {
      window.clearTimeout(timerId)
    }
  }, [phase])

  const handleSubmit = () => {
    if (!validatePhone(phone, getLocaleConfig(locale))) {
      setErrorMessage(t('valid_phone_number_required'))
      return
    }
    const digits = extractDigits(phone)
    setErrorMessage('')
    if (AuthUtil.isLoggedIn()) {
      setJoinWaitlistPhoneNumber(digits)
    }
    onSubmitPhone?.(digits)
    setPhase('resizing')
  }

  const getPhonePlaceholder = (): string => phoneConfig.placeholder

  const wrapperHeight = heights ? (phase === 'capture' ? heights.capture : heights.success) : undefined

  return (
    <Modal
      open={open}
      onClose={handleClose}
      showCloseButton={true}
      className={styles.modal}
      closeButtonClassName={styles.closeButton}
      responsiveBottomSheet={true}
    >
      <div
        className={`${styles.transitionWrapper} ${phase === 'resizing' ? styles.transitionWrapperAnimating : ''}`}
        style={wrapperHeight ? { height: wrapperHeight } : undefined}
      >
        <div
          ref={captureRef}
          className={`${styles.captureContainer} ${phase !== 'capture' ? styles.contentHidden : ''}`}
          aria-hidden={phase !== 'capture'}
        >
          <div className={styles.heading}>{t('waitlist_sms_modal_heading')}</div>
          <div className={styles.divider} />
          <div className={styles.subheading}>{t('waitlist_sms_modal_subheading')}</div>
          <input
            type="tel"
            inputMode="tel"
            className={styles.phoneInput}
            placeholder={getPhonePlaceholder()}
            value={phone}
            onChange={e => {
              setPhone(formatPhoneNumber(e.target.value, phoneConfig.format))
              if (errorMessage) setErrorMessage('')
            }}
            aria-label={getPhonePlaceholder()}
          />
          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
          <div className={styles.consent}>
            {t('waitlist_sms_modal_consent_body')}
            {t('waitlist_sms_modal_consent_legal_prefix')}
            <a
              className={styles.consentLink}
              href={locale === 'en-gb' ? WAITLIST_SMS_VENDOR_TERMS_URL_UK : WAITLIST_SMS_VENDOR_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('waitlist_sms_modal_terms_link_label')}
            </a>
            {t('waitlist_sms_modal_consent_legal_separator')}
            <a
              className={styles.consentLink}
              href={locale === 'en-gb' ? WAITLIST_SMS_VENDOR_PRIVACY_URL_UK : WAITLIST_SMS_VENDOR_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('privacy_policy')}
            </a>
            {t('waitlist_sms_modal_consent_legal_suffix')}
          </div>
          <Button
            className={styles.submitButton}
            type={BUTTON_TYPES.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            round={true}
            onClick={handleSubmit}
          >
            {t('waitlist_sms_modal_notify_me')}
          </Button>
        </div>
        <div
          ref={successRef}
          className={`${styles.successContainer} ${phase === 'success' ? styles.fadeIn : styles.successPending}`}
          aria-hidden={phase !== 'success'}
        >
          <div className={styles.successHeading}>{t('waitlist_sms_modal_success_heading')}</div>
          <div className={styles.successBody}>{t('waitlist_sms_modal_success_body')}</div>
          <Button
            className={styles.submitButton}
            type={BUTTON_TYPES.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            round={true}
            onClick={handleClose}
          >
            {t('continue_shopping')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default WaitlistSmsModalLegacy
